import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import type { SignUpFieldDescriptor } from "@/types/platform.types";
import {
  hasSignUpExtensionValue,
  isExtensionFieldRequired,
  isSignUpFieldVisible,
} from "@/utils/is-extension-field-required";
import { memo, useEffect, useMemo } from "react";
import { useWatch, type UseFormReturn } from "react-hook-form";
import { SignUpAddressField } from "./sign-up-address-field";
import { SignUpCountryField } from "./sign-up-country-field";

export type DynamicSignUpFieldsProps = {
  descriptors: SignUpFieldDescriptor[];
  form: UseFormReturn<any>;
  fieldNamePrefix?: string;
  isFieldReadOnly?: (descriptor: SignUpFieldDescriptor, currentValue: unknown) => boolean;
};

export const DynamicSignUpFields = memo(function DynamicSignUpFields({
  descriptors,
  form,
  fieldNamePrefix = "signUpExtensions",
  isFieldReadOnly,
}: DynamicSignUpFieldsProps) {
  const sorted = useMemo(() => [...descriptors].sort((a, b) => a.order - b.order), [descriptors]);
  const extensionValues = useWatch({ control: form.control, name: fieldNamePrefix }) as
    | Record<string, unknown>
    | undefined;
  const visible = useMemo(
    () => sorted.filter(d => isSignUpFieldVisible(d, extensionValues)),
    [sorted, extensionValues]
  );

  // A field the controlling checkbox just hid must not submit what was typed into it earlier:
  // the checkbox is the answer of record, and a stale value would contradict it in storage.
  useEffect(() => {
    for (const d of sorted) {
      if (isSignUpFieldVisible(d, extensionValues)) continue;
      const path = `${fieldNamePrefix}.${d.name}`;
      const cleared = d.type === "checkbox" ? false : "";
      // Already cleared is the loop's exit: an unchecked checkbox still "has a value" (`false`),
      // so without this the effect would rewrite it and re-trigger its own watch forever.
      const current = form.getValues(path);
      if (current === cleared || !hasSignUpExtensionValue(current)) continue;
      form.setValue(path, cleared, { shouldValidate: false, shouldDirty: true });
    }
  }, [sorted, extensionValues, fieldNamePrefix, form]);

  if (!descriptors.length) return null;

  return (
    <>
      {visible.map(d => {
        const showFirst = (d.metadata?.showFirst as string[]) || [];
        const fieldPath = `${fieldNamePrefix}.${d.name}`;
        const extRequired = isExtensionFieldRequired(d);
        const currentValue = form.getValues(fieldPath);
        const readOnly = isFieldReadOnly?.(d, currentValue) ?? false;

        if (d.type === "address") {
          return (
            <FormField
              key={d.name}
              control={form.control}
              name={fieldPath}
              render={({ field }) => (
                <SignUpAddressField field={field} label={d.label} required={extRequired} readOnly={readOnly} />
              )}
            />
          );
        }

        if (d.type === "country") {
          return (
            <FormField
              key={d.name}
              control={form.control}
              name={fieldPath}
              render={({ field }) => (
                <SignUpCountryField
                  field={field}
                  label={d.label}
                  required={extRequired}
                  readOnly={readOnly}
                  showFirst={showFirst}
                />
              )}
            />
          );
        }

        if (d.type === "selectList") {
          return (
            <FormField
              key={d.name}
              control={form.control}
              name={fieldPath}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {d.label}
                    {extRequired ? " *" : ""}
                  </FormLabel>
                  <div className="grid grid-cols-1 gap-2">
                    {(d.options ?? []).map(opt => (
                      <Button
                        key={opt.value}
                        type="button"
                        variant={field.value === opt.value ? "default" : "outline"}
                        disabled={readOnly}
                        onClick={() => field.onChange(opt.value)}
                      >
                        {opt.label}
                      </Button>
                    ))}
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
          );
        }

        if (d.type === "select") {
          return (
            <FormField
              key={d.name}
              control={form.control}
              name={fieldPath}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {d.label}
                    {extRequired ? " *" : ""}
                  </FormLabel>
                  <Select value={field.value ?? ""} onValueChange={field.onChange} disabled={readOnly}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder={`Select ${d.label.toLowerCase()}`} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {(d.options ?? []).map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          );
        }

        if (d.type === "checkbox") {
          return (
            <FormField
              key={d.name}
              control={form.control}
              name={fieldPath}
              render={({ field }) => (
                <FormItem>
                  <div className="flex items-start space-x-3">
                    <FormControl>
                      <Checkbox
                        checked={Boolean(field.value)}
                        disabled={readOnly}
                        onCheckedChange={v => field.onChange(Boolean(v))}
                      />
                    </FormControl>
                    <FormLabel className="font-normal">
                      {d.label}
                      {extRequired ? " *" : ""}
                    </FormLabel>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
          );
        }

        if (d.type === "textarea") {
          return (
            <FormField
              key={d.name}
              control={form.control}
              name={fieldPath}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {d.label}
                    {extRequired ? " *" : ""}
                  </FormLabel>
                  <FormControl>
                    <Textarea
                      rows={4}
                      disabled={readOnly}
                      placeholder={`Enter ${d.label.toLowerCase()}`}
                      value={(field.value as string) ?? ""}
                      onChange={field.onChange}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          );
        }

        return (
          <FormField
            key={d.name}
            control={form.control}
            name={fieldPath}
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {d.label}
                  {extRequired ? " *" : ""}
                </FormLabel>
                <FormControl>
                  <Input
                    type={d.type === "email" ? "email" : d.rules?.format === "url" ? "url" : "text"}
                    disabled={readOnly}
                    placeholder={`Enter ${d.label.toLowerCase()}`}
                    value={(field.value as string) ?? ""}
                    onChange={field.onChange}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        );
      })}
    </>
  );
});

export default DynamicSignUpFields;
